import { useMemo } from 'react';
import { PLATFORM_LIMITS } from '../../constants/platformCompliance';

const InstagramSettingsPanel = ({ settings, onChange, capabilities, content }) => {
  const caption = settings.captionOverride ?? buildCaption(content);
  const charCount = caption.length;
  const hashtagCount = useMemo(() => (caption.match(/#\w+/g) || []).length, [caption]);
  const limits = PLATFORM_LIMITS.INSTAGRAM;
  const charOver = charCount > limits.maxCaptionChars;
  const hashOver = hashtagCount > limits.maxHashtags;

  return (
    <div className="space-y-4">
      {/* Account Type */}
      {capabilities?.instagramAccountType && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Account Type:</span>
          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
            capabilities.instagramAccountType === 'BUSINESS'
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : capabilities.instagramAccountType === 'CREATOR'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
          }`}>
            {capabilities.instagramAccountType}
          </span>
        </div>
      )}

      {capabilities?.instagramAccountType === 'PERSONAL' && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-xs text-red-700 dark:text-red-300 font-medium">
          Personal accounts cannot publish via the Instagram API. Switch to a Business or Creator account to enable publishing.
        </div>
      )}

      {/* Carousel indicator */}
      {content?.contentType === 'CAROUSEL' && (
        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-xs text-purple-700 dark:text-purple-300">
          This content will be published as a carousel post. Items will be published in the order uploaded.
        </div>
      )}

      {/* Caption Editor */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="label mb-0">Caption</label>
          <div className="flex items-center gap-3 text-xs">
            <span className={charOver ? 'text-red-500 font-medium' : 'text-gray-500'}>
              {charCount} / {limits.maxCaptionChars}
            </span>
            <span className={hashOver ? 'text-red-500 font-medium' : 'text-gray-500'}>
              {hashtagCount} / {limits.maxHashtags} hashtags
            </span>
          </div>
        </div>
        <textarea
          className={`input w-full h-32 resize-none ${charOver || hashOver ? 'border-red-400' : ''}`}
          value={caption}
          onChange={e => onChange({ ...settings, captionOverride: e.target.value })}
          placeholder="Write your Instagram caption..."
        />
        {charOver && (
          <p className="text-xs text-red-500 mt-1">Caption exceeds {limits.maxCaptionChars} character limit</p>
        )}
        {hashOver && (
          <p className="text-xs text-red-500 mt-1">Maximum {limits.maxHashtags} hashtags allowed</p>
        )}
      </div>
    </div>
  );
};

function buildCaption(content) {
  const parts = [content?.title, content?.description, content?.aiCaption, content?.hashtags].filter(Boolean);
  return parts.join('\n\n');
}

export default InstagramSettingsPanel;
